/// <reference types="vite/client" />
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { WizardShell } from "./WizardShell";
import { ConflictReviewModal } from "../entity/ConflictReviewModal";
import { useWizardEntity, shapeManualCandidateReview, buildCandidateDescription, checkExistingMatches } from "./hooks/useWizardEntity";
import { useEntityDimensions } from "./hooks/useEntityDimensions";
import { useIndexDimensions } from "@/hooks/useIndexDimensions";
import { Step0EntityRelation } from "./steps/Step0EntityRelation";
import { Step1Extract } from "./steps/Step1Extract";
import { Step4Review } from "./steps/Step4Review";
import {
  StepRole, StepRevenueStage, StepIsThisYou, namesOverlap,
} from "./steps/QuestionSteps";
import { STEP_LABELS, EXTERNAL_STEP_LABELS, EXTERNAL_STEP_TITLES } from "./types";
import type { ManualSearchCandidate, SelfMatchDecision } from "./types";
import { getRoleOptionsForEntityLevel, resolveRole, EXTERNAL_ROLE_OPTIONS } from "./roleConfig";
import AuthUtils from "@/providers/firebase/authUtils";
import { setAuthJsonCookie, AUTH_USER_COOKIE } from "@/utils/authCookies";
import { hasRelationshipBetween } from "@/services/api/entityApi";
import { fastLine, WAIT_BUDGET_MS } from "@/lib/waitExpectations";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

function entityLevel(entityType: string): string {
  switch (entityType) {
    case "company":
    case "organisation":
    case "governance_body":
      return "L0";
    case "product":
    case "service":
      return "L1";
    case "function":
    case "platform":
      return "L2";
    case "person":
    case "natural_person":
      return "L3";
    default:
      return "L0";
  }
}

interface Wizard_Entity_OnboardingProps {
  open: boolean;
  onClose: () => void;
  onMinimize?: (step: number) => void;
  onEntityCreated?: (entity: {
    entity_id: string;
    entity_name: string;
    entity_type: string;
    parent_entity_ids: string[];
  }) => void;
  parentEntityId?: string | null;
}

// Outer shell: only renders when open so the hook state fully resets each open.
export function Wizard_Entity_Onboarding(props: Wizard_Entity_OnboardingProps) {
  if (!props.open) return null;
  return <WizardInner {...props} />;
}

function WizardInner({ onClose, onMinimize, onEntityCreated, parentEntityId }: Wizard_Entity_OnboardingProps) {
  const navigate = useNavigate();
  const { indexes } = useIndexDimensions();
  const wizard = useWizardEntity({ onClose, onMinimize, onEntityCreated, parentEntityId, entityIndex: indexes.entity_index_dimensions });
  const {
    state, dispatch, back, minimize,
    validateUrl, startSession, confirmDuplicate, openExtractionSocket, confirmCandidates, closeWs,
    searchManualEntity, createEntityFromManualCandidate,
  } = wizard;
  const { getDimensionValues } = useEntityDimensions(indexes.entity_index_dimensions);

  // PROJECT-077 T0112/T0117 — set while the shared merge-review popup is open for a
  // search candidate that has one or more possible existing matches. Cleared once the
  // user picks a match (or "none of these") and the popup's callback continues the flow.
  const [mergeReview, setMergeReview] = useState<{
    candidate: ManualSearchCandidate;
    description: string;
  } | null>(null);

  const extractedEntityType = state.extractedReview?.primary_entity.entity_type ?? state.entityTypeHint ?? "company";
  const level = entityLevel(extractedEntityType);
  // External path uses a fixed, level-independent option list (Client/Supplier/Partner/
  // Investor) instead of the per-level list — see EXTERNAL_ROLE_OPTIONS for why.
  const roleOptions = state.isExternal
    ? EXTERNAL_ROLE_OPTIONS
    : getRoleOptionsForEntityLevel(level, indexes.entity_index_dimensions);

  const selectedRole = state.selectedRoles[0] ?? null;

  // Resolve relationship type + custom title from the dimension-driven role config.
  // For the external path, EXTERNAL_ROLE_OPTIONS must be searched explicitly — its
  // keys (client/supplier) aren't present in every level's own list (e.g. L0 has no
  // client/supplier option), so this is also what actually persists own-vs-external:
  // no separate flag anywhere, just whichever relationship_type this resolves to.
  const [relType, roleTitle] = selectedRole
    ? resolveRole(selectedRole, level, state.roleOther, indexes.entity_index_dimensions, state.isExternal ? EXTERNAL_ROLE_OPTIONS : undefined)
    : ["governance_role" as const, null];

  // T0119 / project077 D-001 — "Yes, that's me" executes the claim AT THE CLICK,
  // loudly: success and failure both surface immediately, and the session's stored
  // entity_id is refreshed to the claimed person (previously the claim fired only
  // at the final Build click, wrapped in a silent catch — failures were invisible
  // and abandoning the wizard lost the claim entirely).
  const [selfMatchClaim, setSelfMatchClaim] = useState<
    { status: "idle" | "claiming" | "claimed" | "failed"; error?: string }
  >({ status: "idle" });

  const runClaimIdentity = async (decision: SelfMatchDecision): Promise<boolean> => {
    if (!decision.isMe || !decision.entityId) return true;
    setSelfMatchClaim({ status: "claiming" });
    try {
      const headers = await AuthUtils.getAuthHeaders();
      const res = await fetch(`${API_URL}/api/v1/entity/claim-identity`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          person_entity_id: decision.entityId,
          company_entity_id: state.entityId,
          role: selectedRole,
          role_title: roleTitle,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.status === false) {
        const message = json?.message ?? `Could not link this profile (HTTP ${res.status})`;
        setSelfMatchClaim({ status: "failed", error: message });
        toast.error(message);
        return false;
      }
      setSelfMatchClaim({ status: "claimed" });
      // The claim merged the old auto-created home entity into this person and
      // archived it — point the session at the survivor now, not at next login.
      const claimedEntityId = json?.data?.entity_id as string | undefined;
      const stored = AuthUtils.getStoredUser();
      if (stored && claimedEntityId && stored.entity_id !== claimedEntityId) {
        setAuthJsonCookie(AUTH_USER_COOKIE, { ...stored, entity_id: claimedEntityId });
      }
      const mergeSummary = json?.data?.merge_summary;
      if (mergeSummary) {
        const moved = [
          mergeSummary.relationships_moved > 0 && `${mergeSummary.relationships_moved} relationship${mergeSummary.relationships_moved === 1 ? "" : "s"}`,
          mergeSummary.permissions_moved > 0 && `${mergeSummary.permissions_moved} permission${mergeSummary.permissions_moved === 1 ? "" : "s"}`,
          mergeSummary.integrations_moved > 0 && `${mergeSummary.integrations_moved} integration${mergeSummary.integrations_moved === 1 ? "" : "s"}`,
        ].filter(Boolean).join(", ");
        toast.success(
          moved
            ? `We merged your existing profile in — ${moved} moved over. Nothing was deleted.`
            : "We merged your existing profile in. Nothing was deleted.",
        );
      } else {
        toast.success("This profile is now linked to your account.");
      }
      return true;
    } catch (e) {
      const message = `Could not link this profile: ${(e as Error).message}`;
      setSelfMatchClaim({ status: "failed", error: message });
      toast.error(message);
      return false;
    }
  };

  const handleSelfMatchDecide = (d: SelfMatchDecision) => {
    dispatch({ type: "SET_SELF_MATCH", decision: d });
    if (d.isMe) void runClaimIdentity(d);
  };

  useEffect(() => () => closeWs(), [closeWs]);

  // Reflect wizard step in the URL so the address bar is not stuck on the
  // underlying page (e.g. /entities) while the wizard modal is open.
  useEffect(() => {
    const originalUrl = window.location.href;
    const url = new URL(originalUrl);
    url.searchParams.set("wizard_step", String(state.step));
    window.history.replaceState({ wizardStep: state.step }, "", url.toString());
    return () => {
      window.history.replaceState({}, "", originalUrl);
    };
  }, [state.step]);

  // ── Step 2 handlers ──────────────────────────────────────────────────────────

  const handleStartExtraction = async (url: string, mode: "company" | "product" | "service" | "person" = "company"): Promise<boolean> => {
    dispatch({ type: "SET_ERROR", error: null });
    dispatch({ type: "SET_URL", url });
    dispatch({ type: "SET_EXTRACTING", value: true });
    try {
      const entityId = await startSession(url, mode, state.visibility);
      // Scan runs in parallel (status shown in the header); the user answers the
      // questions on steps 3–7 while it completes. No auto-advance from the socket.
      await openExtractionSocket(entityId, url, () => {}, state.quickScrape);
      return true;
    } catch (e) {
      const msg = (e as Error).message;
      if (msg === "DUPLICATE_NEEDS_CONFIRMATION") {
        // Fuzzy duplicate: wait for user decision in Step 2. Do not show generic error.
        dispatch({ type: "SET_EXTRACTING", value: false });
        return false;
      }
      dispatch({ type: "SET_EXTRACTING", value: false });
      dispatch({ type: "SET_ERROR", error: msg });
      return false;
    }
  };

  const handleDuplicateDecision = async (forceCreate: boolean) => {
    if (!state.duplicateDecision || state.duplicateDecision.status !== "pending") return;
    // PROJECT-077 — origin/pendingUrl let this same DuplicatePrompt flow serve both
    // the URL path (origin undefined/"url") and the manual-search path ("manual").
    const { origin, pendingUrl, createBody } = state.duplicateDecision;
    dispatch({ type: "SET_EXTRACTING", value: true });
    dispatch({ type: "SET_ERROR", error: null });
    try {
      const entityId = await confirmDuplicate(forceCreate);
      if (origin === "manual") {
        if (pendingUrl) {
          await openExtractionSocket(entityId, pendingUrl, () => {}, state.quickScrape);
        } else {
          const cb = (createBody ?? {}) as Record<string, any>;
          dispatch({
            type: "SET_EXTRACTED_REVIEW",
            review: shapeManualCandidateReview(
              {
                entity_name: cb.entity_name ?? "Unknown",
                entity_type: cb.entity_type ?? state.entityTypeHint,
                entity_type_sub: cb.entity_type_sub ?? null,
                entity_offering_type: cb.entity_offering_type ?? null,
                entity_offering_delivery: cb.entity_offering_delivery ?? null,
                affiliation: null,
                role: null,
                location: null,
                url: null,
                entity_links: null,
                confidence_score: 0.5,
                evidence: cb.entity_description ?? null,
                already_in_raisy: false,
                existing_entity_id: null,
                existing_entity_name: null,
                existing_matches: [],
                dedup_check_skipped: false,
              },
              cb.entity_description ?? "",
              state.entityTypeHint,
            ),
          });
          dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message: "Ready" });
          dispatch({ type: "SET_EXTRACTING", value: false });
        }
      } else {
        await openExtractionSocket(entityId, state.url, () => {}, state.quickScrape);
      }
      dispatch({ type: "SET_STEP", step: 3, direction: 1 });
    } catch (e) {
      dispatch({ type: "SET_EXTRACTING", value: false });
      dispatch({ type: "SET_ERROR", error: (e as Error).message });
    }
  };

  // PROJECT-077 — manual-entry AI web-search resolver's Continue handler. If the
  // chosen candidate resolved a URL, rejoins the normal scrape/review/build flow via
  // openExtractionSocket exactly like the URL path; otherwise populates the review
  // directly from the search candidate (or the raw description for the escape
  // hatch), same as the node_duplicate_found branch already does without a socket.
  //
  // T0112/T0117 — if the resolver found one or more entities that might already be this
  // same person/company, don't silently reuse or silently discard them: open the shared
  // merge-review popup (setMergeReview) and let the user decide. This returns false so
  // Step1Extract's handleContinue does NOT advance the step — handleMergeResolved/
  // handleMergeNoMatch below advance it themselves once the popup completes.
  const handleResolveManual = async (
    candidate: ManualSearchCandidate | null,
    description: string,
  ): Promise<boolean> => {
    dispatch({ type: "SET_ERROR", error: null });
    if (candidate?.existing_matches && candidate.existing_matches.length > 0) {
      setMergeReview({ candidate, description });
      return false;
    }
    // Escape hatch (or an ambiguous search with zero candidates) — the resolver's own
    // dedup never ran here since there's no candidate for it to attach to. This is the
    // ONLY duplicate guard a person entity gets (create_entity's own guard skips persons
    // entirely), so without this check the same real person could be created again on
    // every escape-hatch attempt with no warning at all.
    if (!candidate) {
      const fallbackName = description.slice(0, 60).trim() || "New Entity";
      const matches = await checkExistingMatches(fallbackName, description, state.entityTypeHint);
      if (matches.length > 0) {
        const syntheticCandidate: ManualSearchCandidate = {
          entity_name: fallbackName,
          entity_type: state.entityTypeHint,
          entity_type_sub: null,
          entity_offering_type: null,
          entity_offering_delivery: null,
          affiliation: null,
          role: null,
          location: null,
          url: null,
          entity_links: null,
          confidence_score: 0.5,
          evidence: description,
          already_in_raisy: true,
          existing_entity_id: matches[0].entity_id,
          existing_entity_name: matches[0].entity_name,
          existing_matches: matches,
          dedup_check_skipped: false,
        };
        setMergeReview({ candidate: syntheticCandidate, description });
        return false;
      }
    }
    return finishResolveManual(candidate, description);
  };

  // The actual "create a new entity from this candidate" path — used directly when the
  // resolver found no possible matches at all, and again from handleMergeNoMatch once
  // the user has explicitly said none of the popup's candidates are it.
  const finishResolveManual = async (
    candidate: ManualSearchCandidate | null,
    description: string,
  ): Promise<boolean> => {
    try {
      const entityId = await createEntityFromManualCandidate(
        candidate, description, state.entityTypeHint, state.visibility,
      );
      // A person's `url` is never safe to run a full-site scrape against — raisy_78 is a
      // company/product scraper, not a person-profile scraper, and the resolver bot can
      // (and does) resolve a person's `url` to their employer's homepage rather than a
      // personal profile page. Pointed at a company site, raisy_78 hijacks this entity's
      // name/type into that company (see websocket_extract_node's entity_type override)
      // instead of describing the person. Never scrape for person candidates, full stop.
      if (candidate?.url && candidate.entity_type !== "person") {
        dispatch({ type: "SET_EXTRACTING", value: true });
        await openExtractionSocket(entityId, candidate.url, () => {}, state.quickScrape);
      } else {
        dispatch({
          type: "SET_EXTRACTED_REVIEW",
          review: shapeManualCandidateReview(candidate, description, state.entityTypeHint),
        });
        dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message: "Ready" });
      }
      return true;
    } catch (e) {
      const msg = (e as Error).message;
      if (msg === "DUPLICATE_NEEDS_CONFIRMATION") {
        // Fuzzy duplicate: wait for user decision via DuplicatePrompt. Stay on Step 2.
        return false;
      }
      dispatch({ type: "SET_ERROR", error: msg });
      return false;
    }
  };

  // Popup resolved to "B is the one" / "merge them" — reuse that entity directly, no
  // /entity/create call at all (the merge-apply request already updated its fields per the
  // user's choices).
  //
  // PROJECT-077b — the wizard's remaining questions exist to produce ONE thing that isn't
  // already on the entity: the viewer's relationship to it (handleBuild turns the role
  // answer into an entity_relationships row; the other answers are preferences on an entity
  // that, being pre-existing, already has them). So if that relationship row already
  // exists, there is nothing left to collect — re-asking would just overwrite a role the
  // user set previously with the same wizard. Skip straight to the ecosystem in that case,
  // and run the full wizard when it doesn't.
  const handleMergeResolved = async (finalEntityId: string) => {
    const review = mergeReview;
    setMergeReview(null);
    if (!review) return;
    dispatch({ type: "SET_ENTITY_ID", entityId: finalEntityId });

    const userEntityId = AuthUtils.getStoredUser()?.entity_id;
    const alreadyLinked = userEntityId
      ? await hasRelationshipBetween(userEntityId, finalEntityId)
      : false;

    if (alreadyLinked) {
      onEntityCreated?.({
        entity_id: finalEntityId,
        entity_name: review.candidate.entity_name,
        entity_type: review.candidate.entity_type,
        parent_entity_ids: parentEntityId ? [parentEntityId] : [],
      });
      toast.success(`You're already connected to ${review.candidate.entity_name} — taking you there.`);
      // Land on B's OWN page, not the user's ecosystem: they answered "B is the one I was
      // looking for", so anything rooted at their own person entity (/ecosystem, with or
      // without a focus id) makes them hunt for B again. /entities/:entity_id is the
      // entity's own detail page.
      // Launched from the canvas (parentEntityId set), stay put so the parent's ecosystem
      // can refresh instead — same exit rule handleBuild uses.
      if (!parentEntityId) navigate(`/entities/${finalEntityId}`);
      onClose();
      return;
    }

    dispatch({
      type: "SET_EXTRACTED_REVIEW",
      review: shapeManualCandidateReview(review.candidate, review.description, state.entityTypeHint),
    });
    dispatch({ type: "SET_EXTRACTION_PROGRESS", pct: 100, message: "Ready" });
    dispatch({ type: "SET_STEP", step: state.step + 1, direction: 1 });
  };

  // Popup resolved to "none of these" — fall through to the normal creation path.
  const handleMergeNoMatch = async () => {
    const review = mergeReview;
    setMergeReview(null);
    if (!review) return;
    const proceeded = await finishResolveManual(review.candidate, review.description);
    if (proceeded) {
      dispatch({ type: "SET_STEP", step: state.step + 1, direction: 1 });
    }
  };

  const handleResumeSession = (_sessionId: string, _currentStep: number) => {
    const entityId = state.urlValidation?.entity_id;
    if (entityId) dispatch({ type: "SET_ENTITY_ID", entityId });
  };

  // ── "Build Ecosystem" (final step) — save role + all wizard answers, then navigate ──

  const handleBuild = async () => {
    if (!state.entityId) return;
    dispatch({ type: "SET_SUBMITTING", value: true });
    dispatch({ type: "SET_ERROR", error: null });
    try {
      const headers = await AuthUtils.getAuthHeaders();

      // Sub-entities the user accepted on the Review step were already persisted via
      // confirmCandidates() (called from handleContinue when leaving that step, before
      // this ever runs). Do NOT re-create them here — that would produce duplicates.

      const selfMatch = state.selfMatchDecision;
      const userEntityId = AuthUtils.getStoredUser()?.entity_id;

      if (selfMatch?.isMe && selfMatch.entityId) {
        // "Yes, that's me" — the claim already executed at the Identity-step click
        // (T0119 / project077 D-001). This is only a loud fallback for a click-time
        // attempt that failed or is still in flight; the endpoint is idempotent.
        // The claim sets identity_user_id + owner permission, merges the caller's
        // previous home entity into the claimed one server-side (PROJECT-077b —
        // relationships/permissions/integrations move over, the old bare entity is
        // archived, never deleted), and creates the person→company relationship.
        if (selfMatchClaim.status === "idle" || selfMatchClaim.status === "failed") {
          await runClaimIdentity(selfMatch);
        }
      } else {
        // "No, not me" or no self-match — extracted entity stays ownerless.
        // Create the role relationship from the user's own entity to the company.
        if (userEntityId && selectedRole) {
          await fetch(`${API_URL}/api/v1/entity/relationship/add`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...headers },
            body: JSON.stringify({
              entity_id: userEntityId,
              related_entity_id: state.entityId,
              relationship_type: relType,
              role_title: roleTitle,
              role_key: selectedRole,
            }),
          }).catch(() => {});
        }
      }

      // Save all wizard answers against the PRIMARY entity we just onboarded.
      // Build scope → its own column; use cases, revenue stage and first task
      // → preferences[user_id] (see entity_management.py).
      // Self-referential (own path only) — these steps never render on the external
      // path, so state.buildScope/selectedUseCases/etc. would already be empty/default
      // there too; the explicit guard just makes that intent unambiguous.
      if (!state.isExternal) {
        const hasAnswers =
          state.buildScope ||
          state.selectedUseCases.length > 0 ||
          state.revenueStage ||
          state.firstTasks.length > 0;
        if (state.entityId && hasAnswers) {
          fetch(`${API_URL}/api/v1/entity/update`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...headers },
            body: JSON.stringify({
              entity_id: state.entityId,
              ...(state.buildScope ? { build_scope: state.buildScope } : {}),
              ...(selectedRole ? { selected_role: selectedRole } : {}),
              ...(state.selectedUseCases.length > 0 ? { selected_use_cases: state.selectedUseCases } : {}),
              ...(state.revenueStage ? { revenue_stage: state.revenueStage } : {}),
              ...(state.firstTasks.length > 0 ? { first_tasks: state.firstTasks } : {}),
              ...(state.firstTasks.includes("other") && state.firstTaskOther.trim()
                ? { first_task_other: state.firstTaskOther.trim() }
                : {}),
            }),
          }).catch(() => {}); // non-critical
        }
      }

      // If launched from the ecosystem canvas, create the parent-child relationship.
      // The backend create already handles parent_weights when parent_entity_id is sent,
      // but the explicit relationship ensures the canvas edge exists.
      if (parentEntityId && state.entityId) {
        await fetch(`${API_URL}/api/v1/entity/relationship/add`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headers },
          body: JSON.stringify({
            entity_id: parentEntityId,
            related_entity_id: state.entityId,
            relationship_type: "parent_child",
          }),
        }).catch(() => {});
      }

      // 4. Notify caller and close. If launched from the ecosystem canvas
      // (parentEntityId set), stay on the current page so the parent entity's
      // ecosystem can be refreshed; otherwise navigate to the new entity.
      onEntityCreated?.({
        entity_id: state.entityId,
        entity_name: state.extractedReview?.primary_entity.suggested_name ?? state.entityDetails?.name ?? "",
        entity_type: state.extractedReview?.primary_entity.entity_type ?? state.entityTypeHint ?? "company",
        parent_entity_ids: parentEntityId ? [parentEntityId] : [],
      });
      if (parentEntityId) {
        onClose();
      } else {
        // Land on the entity that was just onboarded, not the user's own ecosystem root.
        // This is the exit for BOTH a brand-new entity and a confirmed existing one (the
        // "B is the one I was looking for" path reaches handleBuild whenever no
        // relationship existed yet and the wizard therefore ran in full) — in both cases
        // the thing the user just finished working on is the entity, so show them that.
        // Using the entity's own /entities/:entity_id page also removes the reason the
        // external path used to divert to the flat list: that page isn't rooted in the
        // user's graph, so an external entity is just as much at home there.
        navigate(`/entities/${state.entityId}`);
        onClose();
      }
    } catch (e) {
      dispatch({ type: "SET_ERROR", error: (e as Error).message });
    } finally {
      dispatch({ type: "SET_SUBMITTING", value: false });
    }
  };

  // ── Step rendering (one question per page) ──────────────────────────────────
  const scanIsDone = !state.isExtracting && state.extractedReview !== null;

  // Measured-scan celebration: when the background site scan finishes well under the
  // p90 worst-case budget, surface "done in Xs — N% faster" in the persistent header
  // ScanStatus. Budgets come from lib/waitExpectations (measured on raisy.team
  // 2026-09-02, not guessed). Fires once per scan; resets if the user re-scans.
  const scanStartRef = useRef<number | null>(null);
  const scanDoneRef = useRef(false);
  const [scanCelebrate, setScanCelebrate] = useState<string | null>(null);
  useEffect(() => {
    if (state.isExtracting && scanStartRef.current === null) {
      scanStartRef.current = Date.now();
      scanDoneRef.current = false;
      setScanCelebrate(null);
    }
  }, [state.isExtracting]);
  useEffect(() => {
    if (scanIsDone && scanStartRef.current !== null && !scanDoneRef.current) {
      scanDoneRef.current = true;
      setScanCelebrate(fastLine("Scan complete", Date.now() - scanStartRef.current, WAIT_BUDGET_MS.wizardScan));
    }
  }, [scanIsDone]);

  const userDisplayName = AuthUtils.getStoredUser()?.displayName ?? "";

  // Step 8 is injected only when extraction found a person entity matching the user.
  const matchedPerson = useMemo(() => {
    if (!scanIsDone || !userDisplayName) return null;
    return state.detectedPersonEntities.find((p) => namesOverlap(userDisplayName, p.entity_name)) ?? null;
  }, [scanIsDone, userDisplayName, state.detectedPersonEntities]);

  // Own path (D-061 lean): 1 Type · 2 Company · 3 Role · 4 Stage · 5 Review (+6 Identity,
  // if a self-match was found). Goals / Build scope / First task are DISABLED (defaulted).
  // External path is unchanged: its own Review sits at step 4, already the last step.
  const totalSteps = state.isExternal ? 4 : (matchedPerson ? 6 : 5);
  const stepLabels = state.isExternal
    ? EXTERNAL_STEP_LABELS
    : (matchedPerson ? [...STEP_LABELS, "Identity"] : STEP_LABELS);

  // Step 1 (own vs. external) and Step 2 (URL) each own their own Continue/Back button;
  // questions 3–N use the shared footer.
  const isQuestionStep = state.step >= 3 && state.step <= totalSteps;
  const isLastStep = state.step === totalSteps;
  // Review step (backlog #18 discovery review) — external path reuses step 4 (its last
  // step); own-path Review is step 5 (the accept gate before Build).
  const isReviewStep = state.isExternal ? state.step === 4 : state.step === 5;

  // Per-step Continue gating.
  const canContinue = (() => {
    switch (state.step) {
      case 3: {
        if (state.selectedRoles.length === 0) return false;
        const otherOk = !state.selectedRoles.includes("other") || state.roleOther.trim().length > 0;
        return otherOk;
      }
      case 4: {
        if (state.isExternal) return scanIsDone; // external review step — just needs the scan done
        return state.revenueStage !== ""; // Stage — the raise-benchmark source
      }
      case 5: return scanIsDone; // own-path review step — just needs the scan done
      // identity — a decision is required, and a "yes" claim in flight must land first
      case 6: return state.selfMatchDecision !== null && selfMatchClaim.status !== "claiming";
      default: return false;
    }
  })();

  const handleContinue = async () => {
    if (isReviewStep) {
      try {
        await confirmCandidates();
      } catch {
        return; // error surfaced inline on the Review step — stay put
      }
    }
    if (isLastStep) {
      handleBuild();
    } else {
      dispatch({ type: "SET_STEP", step: state.step + 1, direction: 1 });
    }
  };

  const continueLabel = isLastStep
    ? (scanIsDone ? (state.isExternal ? "Confirm & Save" : "Build Ecosystem") : "Waiting for scan…")
    : "Continue";

  const renderStep = () => {
    switch (state.step) {
      case 1:
        return <Step0EntityRelation dispatch={dispatch} isExternal={state.isExternal} />;
      case 3:
        return (
          <StepRole
            options={roleOptions}
            selectedRoles={state.selectedRoles}
            onSelectRole={(role) => dispatch({ type: "SET_ROLE", role })}
            roleOther={state.roleOther}
            onSetRoleOther={(value) => dispatch({ type: "SET_ROLE_OTHER", value })}
          />
        );
      case 4:
        return state.isExternal ? (
          <Step4Review
            state={state}
            isExtracting={state.isExtracting}
            onToggleCandidate={(i) => dispatch({ type: "TOGGLE_CANDIDATE", index: i })}
            confirmError={state.confirmCandidatesError}
          />
        ) : (
          <StepRevenueStage
            revenueStage={state.revenueStage}
            onSetRevenueStage={(val) => dispatch({ type: "SET_REVENUE_STAGE", value: val })}
          />
        );
      case 5:
        return (
          <Step4Review
            state={state}
            isExtracting={state.isExtracting}
            onToggleCandidate={(i) => dispatch({ type: "TOGGLE_CANDIDATE", index: i })}
            confirmError={state.confirmCandidatesError}
          />
        );
      case 6:
        return matchedPerson ? (
          <StepIsThisYou
            matchedPerson={matchedPerson}
            decision={state.selfMatchDecision}
            onDecide={handleSelfMatchDecide}
            claimState={selfMatchClaim}
            onRetryClaim={() => state.selfMatchDecision && void runClaimIdentity(state.selfMatchDecision)}
          />
        ) : null;
      default:
        return (
          <Step1Extract
            state={state}
            dispatch={dispatch}
            onValidateUrl={validateUrl}
            onStartExtraction={handleStartExtraction}
            onDuplicateDecision={handleDuplicateDecision}
            onResumeSession={handleResumeSession}
            onClose={onClose}
            onSearchManual={(description, clarifications) =>
              searchManualEntity(description, state.entityTypeHint, clarifications)
            }
            onResolveManual={handleResolveManual}
          />
        );
    }
  };

  return (
    <>
      <WizardShell
        step={state.step}
        direction={state.direction}
        total={totalSteps}
        stepLabels={stepLabels}
        title={state.isExternal ? EXTERNAL_STEP_TITLES[state.step] : undefined}
        onBack={() => back(state.step)}
        onContinue={handleContinue}
        continueLabel={continueLabel}
        continueDisabled={!canContinue}
        isLoading={state.isSubmitting || state.isConfirmingCandidates}
        onMinimize={() => minimize(state.step)}
        onClose={onClose}
        hideFooter={!isQuestionStep}
        scan={{
          pct: state.extractionPct,
          message: state.extractionMessage,
          isExtracting: state.isExtracting,
          isDone: scanIsDone,
          celebrateLine: scanCelebrate ?? undefined,
        }}
      >
        {renderStep()}
      </WizardShell>

      {mergeReview && (
        <ConflictReviewModal
          open={true}
          onClose={() => setMergeReview(null)}
          onResolved={handleMergeResolved}
          onNoMatch={handleMergeNoMatch}
          matches={mergeReview.candidate.existing_matches}
          candidateData={{
            entity_name: mergeReview.candidate.entity_name,
            entity_description: buildCandidateDescription(mergeReview.candidate, mergeReview.description),
            entity_type_sub: mergeReview.candidate.entity_type_sub,
            entity_offering_type: mergeReview.candidate.entity_offering_type,
            entity_offering_delivery: mergeReview.candidate.entity_offering_delivery,
            person_role: mergeReview.candidate.role,
            entity_location: mergeReview.candidate.location,
          }}
          candidateSource="search_resolver"
        />
      )}
    </>
  );
}
